import React, { useState, useEffect, useRef } from "react";
import {
  FiAlertTriangle,
  FiFilter,
  FiRefreshCw,
  FiCalendar,
  FiMapPin,
  FiUserCheck,
  FiChevronDown,
  FiChevronUp,
  FiShield,
  FiCheck,
} from "react-icons/fi";
import api from "../../../../connector/api";

export default function StudentBehaviorTab({ student }) {
  const [records, setRecords] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});

  // Custom Dropdown State & Ref
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close custom dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!student?.id) return;

    const fetchBehaviorData = async () => {
      setLoading(true);
      try {
        const res = await api.get(
          `/students/${student.id}/behavior-log/?school_year_id=${selectedYear}`
        );
        setRecords(res.data.records || []);
        if (res.data.school_years) {
          setSchoolYears(res.data.school_years);
        }
      } catch (err) {
        console.error("Failed to fetch behavior records:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBehaviorData();
  }, [student?.id, selectedYear]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getInfractionBadge = (level) => {
    switch (level?.toLowerCase()) {
      case "major":
      case "level_3":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-200/60">
            Major Infraction
          </span>
        );
      case "moderate":
      case "level_2":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200/60">
            Moderate Infraction
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200/60">
            Minor Infraction
          </span>
        );
    }
  };

  // Label finder for the custom dropdown header
  const getSelectedYearLabel = () => {
    if (selectedYear === "all") return "All School Years";
    const found = schoolYears.find((sy) => String(sy.id) === String(selectedYear));
    return found ? found.name : "All School Years";
  };

  return (
    <div className="space-y-4 text-xs animate-in fade-in duration-150">
      {/* Top Header & Filter Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-zinc-100">
        <h3 className="font-bold text-zinc-900 text-xs flex items-center gap-1.5">
          <FiShield className="w-3.5 h-3.5 text-zinc-500" /> Behavior & Disciplinary Logs
        </h3>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          {/* Custom Stylized Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              className="flex items-center gap-2 bg-zinc-50 hover:bg-zinc-100/80 text-zinc-800 px-3 py-1.5 rounded-lg border border-zinc-200/80 transition-all text-xs font-semibold shadow-2xs cursor-pointer focus:outline-none"
            >
              <FiFilter className="w-3 h-3 text-zinc-400" />
              <span className="text-[11px] font-medium text-zinc-500">School Year:</span>
              <span>{getSelectedYearLabel()}</span>
              <FiChevronDown
                className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Dropdown Menu Panel */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-1.5 w-48 bg-white border border-zinc-200/90 rounded-lg shadow-lg z-20 py-1 text-xs animate-in fade-in slide-in-from-top-1 duration-150">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedYear("all");
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-3.5 py-2 flex items-center justify-between transition-colors cursor-pointer ${
                    selectedYear === "all"
                      ? "bg-zinc-50 font-bold text-zinc-900"
                      : "text-zinc-600 hover:bg-zinc-50/80 hover:text-zinc-900"
                  }`}
                >
                  <span>All School Years</span>
                  {selectedYear === "all" && <FiCheck className="w-3.5 h-3.5 text-zinc-800" />}
                </button>

                {schoolYears.length > 0 && <div className="my-1 border-t border-zinc-100" />}

                {schoolYears.map((sy) => {
                  const isSelected = String(selectedYear) === String(sy.id);
                  return (
                    <button
                      key={sy.id}
                      type="button"
                      onClick={() => {
                        setSelectedYear(sy.id);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3.5 py-2 flex items-center justify-between transition-colors cursor-pointer ${
                        isSelected
                          ? "bg-zinc-50 font-bold text-zinc-900"
                          : "text-zinc-600 hover:bg-zinc-50/80 hover:text-zinc-900"
                      }`}
                    >
                      <span>{sy.name}</span>
                      {isSelected && <FiCheck className="w-3.5 h-3.5 text-zinc-800" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {loading && (
            <span className="text-zinc-400 flex items-center gap-1 text-[11px]">
              <FiRefreshCw className="w-3 h-3 animate-spin" /> Syncing...
            </span>
          )}
        </div>
      </div>

      {/* Behavior Logs List / Table */}
      <div className="border border-zinc-200/80 rounded-lg overflow-hidden bg-white shadow-2xs">
        {loading ? (
          <div className="py-8 text-center text-zinc-400">
            <FiRefreshCw className="w-4 h-4 animate-spin inline mr-2" />
            Loading behavior records...
          </div>
        ) : records.length === 0 ? (
          <div className="py-8 text-center text-zinc-400">
            No disciplinary behavior records found for the selected school year.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {records.map((item) => {
              const isExpanded = expandedRows[item.id];
              return (
                <div key={item.id} className="transition-colors hover:bg-zinc-50/50">
                  {/* Summary Bar */}
                  <div
                    onClick={() => toggleRow(item.id)}
                    className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {getInfractionBadge(item.infraction_level)}
                        <span className="font-bold text-zinc-900 text-xs">
                          {item.violation_type || "General Behavior Referral"}
                        </span>
                        {item.school_year_name && (
                          <span className="text-[10px] font-medium text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded">
                            {item.school_year_name} {item.term_name ? `• ${item.term_name}` : ""}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-[11px] text-zinc-500 flex-wrap pt-0.5">
                        <span className="flex items-center gap-1 font-mono">
                          <FiCalendar className="w-3 h-3 text-zinc-400" />
                          {item.date_of_incident || "—"} {item.time_of_incident ? `at ${item.time_of_incident}` : ""}
                        </span>
                        {item.location && (
                          <span className="flex items-center gap-1">
                            <FiMapPin className="w-3 h-3 text-zinc-400" /> {item.location}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <FiUserCheck className="w-3 h-3 text-zinc-400" /> Referred by:{" "}
                          <strong className="text-zinc-700">
                            {item.referring_teacher_detail || item.referring_teacher_name || "Unknown"}
                          </strong>
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
                      {item.parent_contacted && (
                        <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200/60">
                          Parent Contacted
                        </span>
                      )}
                      <button className="text-zinc-400 hover:text-zinc-600 transition-colors cursor-pointer">
                        {isExpanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Detail Panel */}
                  {isExpanded && (
                    <div className="px-3.5 pb-3.5 pt-1 bg-zinc-50/70 border-t border-zinc-100 space-y-3">
                      <div>
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block mb-1">
                          Incident Description
                        </span>
                        <p className="text-zinc-700 bg-white p-2.5 rounded-lg border border-zinc-200/70 leading-relaxed">
                          {item.incident_description || "No detailed description logged."}
                        </p>
                      </div>

                      {/* Interventions Section */}
                      {item.interventions && item.interventions.length > 0 && (
                        <div>
                          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block mb-1">
                            Prior Classroom Interventions ({item.interventions.length})
                          </span>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {item.interventions.map((int, idx) => (
                              <div
                                key={int.id || idx}
                                className="bg-white p-2 rounded-lg border border-zinc-200/70 text-[11px]"
                              >
                                <div className="font-semibold text-zinc-800">
                                  {int.intervention_type}
                                </div>
                                <div className="text-zinc-400 text-[10px] font-mono">
                                  Attempted: {int.date_attempted || "—"}
                                </div>
                                {int.other_details && (
                                  <div className="text-zinc-600 mt-1">{int.other_details}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* VP / Admin Notes */}
                      {item.vp_notes && (
                        <div>
                          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block mb-1">
                            Administrative / VP Notes
                          </span>
                          <p className="text-zinc-700 bg-amber-50/50 p-2.5 rounded-lg border border-amber-200/60 leading-relaxed">
                            {item.vp_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}